import { ChatDetailsResponse } from '@choco-admin/types';
import { Chip, Avatar, Button, Switch } from '@heroui/react';
import React, { useCallback } from 'react';
import { FiTruck, FiExternalLink, FiBell, FiMail } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useUpdateChatNotificationPreferences } from '../../../../../hooks/useUpdateChatNotificationPreferences';

interface ChatDetailsWithNotifications extends ChatDetailsResponse {
  newOrder?: {
    deselectedUsersList?: string[];
  };
}

interface SupplierSectionProps {
  chatData: ChatDetailsWithNotifications;
  chatId: string;
}

export const SupplierSection: React.FC<SupplierSectionProps> = ({
  chatData,
  chatId,
}) => {
  const supplier = chatData.supplierEntity;
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { updateNotificationPreferences, isUpdating } =
    useUpdateChatNotificationPreferences({
      onSuccess: () => {
        addNotification({
          title: 'Notification Preferences Updated',
          message: 'Order notifications have been updated for this chat.',
          type: 'success',
        });
      },
      onError: (error) => {
        addNotification({
          title: 'Update Failed',
          message: error.message,
          type: 'error',
        });
      },
    });

  const handleNotificationChange = useCallback(
    async (isSelected: boolean) => {
      if (!supplier?.id) return;

      await updateNotificationPreferences({
        chatId,
        supplierId: supplier.id,
        isEnabled: isSelected,
      });
    },
    [chatId, supplier?.id, updateNotificationPreferences]
  );

  const handleSupplierClick = () => {
    if (supplier?.id) {
      navigate(`/suppliers/${supplier.id}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Supplier Info */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <FiTruck className="w-4 h-4 text-default-400" />
          <p className="text-xs font-medium text-default-500 uppercase tracking-wide">
            Primary Distributor
          </p>
        </div>

        {supplier ? (
          <div
            className="group relative overflow-hidden rounded-xl border border-default-200 bg-content1 p-4 transition-all hover:border-primary/50 hover:shadow-md cursor-pointer"
            onClick={handleSupplierClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSupplierClick();
              }
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar
                  name={supplier.name}
                  className="h-12 w-12 text-lg"
                  isBordered
                  color="primary"
                  radius="lg"
                />
                <div>
                  <h4 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                    {supplier.name}
                  </h4>
                  {supplier.referenceId && (
                    <p className="text-sm text-default-500">
                      ID:{' '}
                      <span className="font-mono">{supplier.referenceId}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Chip
                  color="success"
                  variant="flat"
                  size="sm"
                  className="capitalize"
                >
                  Active
                </Chip>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <FiExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-default-300 bg-default-50 p-6 text-center">
            <FiTruck className="mx-auto h-8 w-8 text-default-300 mb-2" />
            <p className="text-default-500">No distributor assigned</p>
          </div>
        )}
      </div>

      {/* Notification Preferences */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <FiBell className="w-4 h-4 text-default-400" />
          <p className="text-xs font-medium text-default-500 uppercase tracking-wide">
            Notification Preferences
          </p>
        </div>

        <div className="rounded-xl border border-default-200 bg-content1 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-base font-semibold text-foreground mb-1">
                Order Notifications
              </h4>
              <div className="flex items-center gap-2 text-sm text-default-500">
                <FiMail className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  {chatData.supplierEntity?.name ? (
                    `${chatData.supplierEntity.name} notifications`
                  ) : (
                    <span className="italic">No email configured</span>
                  )}
                </span>
              </div>
            </div>
            <Switch
              isSelected={
                chatData.newOrder?.deselectedUsersList === undefined ||
                chatData.newOrder?.deselectedUsersList?.length === 0
              }
              onValueChange={handleNotificationChange}
              isDisabled={isUpdating || !supplier?.id}
              size="lg"
              aria-label="Enable order notifications"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
